import React, { useEffect, useMemo, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { ChromePicker } from "react-color";
import Popover from "@material-ui/core/Popover";
import {
    ColorValue,
    IBinding,
    ISelectableValue,
    IValueOption,
} from "../../../constants/product-customizations/customization";
import {
    CustomizationSelectionType,
    TColorPickerCustomizationSelection,
    TColorSwatchCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import { formatMessage } from "lib/globalize-util";
import classnames from "classnames";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import { assertExhaustiveSwitch } from "components/utils/validations";
import { themeOptions } from "styles/theme";

const TRANSPARENT_COLOR = "#000000000";

interface ITransparentProps {
    onClick: (event?: any) => void;
}

const Transparent: React.FC<ITransparentProps> = ({ onClick }) => {
    const styles = useMemo(
        () => ({
            noColor: {
                width: "100px",
                height: "40px",
                border: `2px solid ${themeOptions.palette.darkGrey}`,
                cursor: "pointer",
                background: "linear-gradient(160deg, #ffffff 0%,#ffffff 49.6%,#FF0000FF 50%,#ffffff 53%,#ffffff 100%)",
            },
        }),
        [],
    );

    return <div data-test={CUSTOMIZATIONS.TRANSPARENT_COLOR_VIEW} style={styles.noColor} onClick={onClick} />;
};

type SelectionTypes =
    | ({ type: CustomizationSelectionType.COLOR_PICKER } & TColorPickerCustomizationSelection)
    | ({ type: CustomizationSelectionType.COLOR_SWATCH } & TColorSwatchCustomizationSelection);

type TSelectionProps = {
    attributeId: string;
    label: string;
    target?: IBinding;
};

type TProps<T, ValueKeyField = {}, Selected = T & TSelectionProps & ValueKeyField> = (ValueKeyField &
    TSelectionProps) & {
    isRequired?: boolean | undefined;
    readonly?: boolean;
    picker?: ColorValue;
    swatch?: ISelectableValue;
    selected: Selected;
    onChange: (value: Selected) => void;
    onValidationError?: (id: string, error: string | null) => void;
    isNested?: boolean;
};

type TColorAttributeProps<T, ValueKey> = TProps<T> | TProps<T, { valueKey: ValueKey }>;

export const ColorAttribute = <T, ValueKey>(props: TColorAttributeProps<T & SelectionTypes, ValueKey>) => {
    const {
        selected,
        picker,
        swatch,
        attributeId,
        label,
        isRequired,
        target,
        readonly = false,
        onValidationError,
        isNested = false,
    } = props;

    const [anchorPickerEl, setAnchorPickerEl] = useState<HTMLElement | null>(null);
    const [selectedColor, setSelectedColor] = useState<string>("");
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [selectedColorSwatch, setSelectedColorSwatch] = useState<IValueOption | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        let value;
        if (!selected) {
            value = TRANSPARENT_COLOR;
        } else {
            value = selected.value || TRANSPARENT_COLOR;
            if (selected.valueId) {
                setSelectedColorSwatch(swatch?.values?.find((s) => s._id === selected.valueId) ?? null);
            } else {
                setSelectedColorSwatch(null);
            }
        }

        const isInvalid = isRequired && (!value || value === TRANSPARENT_COLOR);
        setValidationError(!isInvalid ? null : formatMessage("Please select color"));
        setSelectedColor(value);
    }, [selected, picker, swatch, isRequired]);

    useEffect(() => {
        onValidationError?.(attributeId, validationError ?? null);
    }, [validationError, onValidationError, attributeId]);

    const classes = useStyles({ isNested });

    const styles = useMemo(() => getStyles({ readonly, isNested }), [readonly, isNested]);

    const handleChange = (value: string, index: number | undefined = -1) => {
        if (readonly) {
            return;
        }

        const option = swatch?.values?.[index];
        if (value !== TRANSPARENT_COLOR) {
            setValidationError?.(null);
        } else if (isRequired && (!value || value === TRANSPARENT_COLOR)) {
            setValidationError(formatMessage("Please select color"));
        }

        if (value === TRANSPARENT_COLOR) {
            value = "";
        }

        const result = <T, Rest>(selected: T, rest: Rest) => ({
            ...selected,
            attributeId,
            label,
            value,
            target,
            ...rest,
        });

        const callOnChange = <Rest,>(rest: Rest) => {
            if ("valueKey" in props) {
                props.onChange(result(props.selected, { ...rest, valueKey: props.valueKey }));
            } else {
                props.onChange(result(props.selected, rest));
            }
        };

        switch (selected.type) {
            case CustomizationSelectionType.COLOR_SWATCH:
                callOnChange({
                    valueId: option?._id ?? "",
                    displayValue: option?.name ?? "",
                });

                break;

            case CustomizationSelectionType.COLOR_PICKER:
                callOnChange({ displayValue: value });
                break;

            default:
                assertExhaustiveSwitch(selected);
        }
    };

    const handlePickerChange = ({ hex }: { hex: string }) => {
        setSelectedColor(hex);
        handleChange(hex);
    };

    const handleColorViewClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
        if (readonly) {
            return;
        }

        setShowPopup(true);
        setAnchorPickerEl(event.currentTarget);
    };

    const handleColorViewClose = () => {
        setShowPopup(false);
        setAnchorPickerEl(null);
    };

    const handleNoColorCLick = () => {
        setSelectedColor(TRANSPARENT_COLOR);
        handleChange(TRANSPARENT_COLOR);
        setSelectedColorSwatch(null);
        handleColorViewClose();
    };

    const handleSwatchClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        const indexDataKey = e.currentTarget.getAttribute("data-key");
        if (indexDataKey == null) {
            return;
        }

        const index = Number(indexDataKey);
        const selectedSwatch = swatch?.values?.[Number(index)];
        if (!selectedSwatch) {
            return;
        }

        const rgbColor = selectedSwatch.color?.rgb || "";

        setSelectedColorSwatch(selectedSwatch);
        setSelectedColor(rgbColor);
        handleChange(rgbColor, index);
    };

    return (
        <div data-test={`${CUSTOMIZATIONS.CUSTOMIZATION_MODAL_COLOR_ATTRIBUTE_WRAPPER}`}>
            <div
                data-test={`${CUSTOMIZATIONS.CUSTOMIZATION_MODAL_COLOR_VIEW}`}
                onClick={handleColorViewClick}
                style={{ ...styles.colorPickerView, backgroundColor: selectedColor }}
                className={selectedColor === TRANSPARENT_COLOR ? classes.noColor : ""}
                title={selectedColorSwatch ? selectedColorSwatch.name : selectedColor}
            />
            {selectedColorSwatch && (
                <div data-test={CUSTOMIZATIONS.COLOR_SWATCHES_LABEL} className={classes.colorSwatchName}>
                    {selectedColorSwatch.name}
                </div>
            )}
            {validationError && <div className={classes.validationError}>{validationError}</div>}

            <Popover
                style={styles.popover}
                open={showPopup}
                anchorEl={anchorPickerEl}
                onClose={handleColorViewClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                {!isRequired && (
                    <div style={styles.noColorContainer}>
                        <span style={styles.noColorTitle}>
                            {formatMessage("Please click to select transparent color")}
                        </span>
                        <Transparent onClick={handleNoColorCLick} />
                    </div>
                )}
                {picker && (
                    <div data-test={`${CUSTOMIZATIONS.COLOR_ATTRIBUTE_COLOR_PICKER}`}>
                        <ChromePicker
                            color={selectedColor}
                            onChange={handlePickerChange}
                            disableAlpha={true}
                            styles={styles.picker}
                        />
                    </div>
                )}

                {swatch?.values && (
                    <div data-test={`${CUSTOMIZATIONS.COLOR_SWATCHES}`} style={styles.colorSwatches}>
                        {swatch.values.map((swatchValue, index) => (
                            <div key={index} className={classes.colorSwatchItemWrapper}>
                                <div
                                    data-test={`${CUSTOMIZATIONS.COLOR_SWATCH_ITEM}_${index}`}
                                    data-key={index}
                                    onClick={handleSwatchClick}
                                    className={classnames(
                                        swatchValue === selectedColorSwatch ? classes.selectedSwatch : "",
                                    )}
                                    style={{
                                        ...styles.colorSwatchItem,
                                        backgroundColor: swatchValue.color?.rgb,
                                    }}
                                />
                                <div className={classes.colorSwatchItemColorName}>{swatchValue.name}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Popover>
        </div>
    );
};

const useStyles = (config: { isNested: boolean }) =>
    makeStyles({
        selectedSwatch: { border: `2px solid ${themeOptions.palette.darkGrey}` },
        noColor: {
            border: `1px solid ${themeOptions.palette.darkGrey}`,
            background:
                "linear-gradient(160deg, #ffffff 0%,#ffffff 49.6%,#FF0000FF 50%,#ffffff 53%,#ffffff 100%) !important",
        },
        colorSwatchItemWrapper: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "10px",
        },
        colorSwatchItemColorName: {
            color: themeOptions.palette.darkGrey,
        },
        colorSwatchName: {
            marginInlineStart: 0,
            width: config.isNested ? 100 : 144,
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: themeOptions.palette.textColor,
            fontSize: 14,
            fontWeight: 600,
            textAlign: "center",
            textOverflow: "ellipsis",
        },
        validationError: {
            marginLeft: config.isNested ? "7px" : "20px",
            color: themeOptions.palette.errorColor,
            width: "280px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
    } as const)();

const getStyles = (config: { isNested?: boolean; readonly?: boolean }) =>
    ({
        picker: {
            default: {
                picker: {
                    boxShadow: "none",
                },
                body: {
                    paddingBottom: "6px",
                },
                toggles: {
                    paddingTop: "3px",
                },
            },
            disableAlpha: {
                swatch: {
                    width: "15px",
                    height: "15px",
                },
            },
        },
        colorPickerView: {
            marginLeft: config.isNested ? 0 : "20px",
            marginBottom: "5px",
            width: "100px",
            height: "40px",
            cursor: config.readonly ? "auto" : "pointer",
            border: `1px solid ${themeOptions.palette.darkGrey}`,
            borderRadius: "5px",
        },
        popover: {
            zIndex: 5000,
        },
        colorSwatches: {
            display: "flex",
            flexWrap: "wrap" as "wrap",
            margin: "auto",
            width: "500px",
        },
        colorSwatchItem: {
            margin: "10px",
            width: "50px",
            height: "40px",
            borderRadius: "3px",
            cursor: "pointer",
            border: `1px solid ${themeOptions.palette.lightestGrey}`,
        },
        noColorContainer: {
            display: "flex",
            padding: "10px",
        },
        noColorTitle: {
            width: "105px",
        },
    }) as const;
